// ============================================================================
// LoginPage — unified sign-in for UIF / AGC / AMP roles.
// ============================================================================
// Single form for all three role types. On success, populates AuthContext
// (via useAuth().login) and routes the user to their role-appropriate view:
//
//   uif → #admin-dashboard (existing AdminPanel; gets calendar+budget tabs in 3d)
//   agc → #agc             (AgcCalendarPlaceholder for now; full UI in 3d)
//   amp → #amp-events      (AmpCalendarPlaceholder for now; full UI in 3d)
//
// Replaces the prior AdminLogin component. AdminLogin.tsx remains in the tree
// as a deprecated reference; will be removed in Phase 3e cleanup.
// ============================================================================

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../auth';

interface LoginPageProps {
  onCancel: () => void;
}

function routeFor(role: 'uif' | 'amp' | 'agc'): string {
  if (role === 'uif') return '#admin-dashboard';
  if (role === 'agc') return '#agc';
  return '#amp-events';
}

export function LoginPage({ onCancel }: LoginPageProps) {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  // Task #2: inline "Forgot password?" mode. Asks for email and sends a
  // reset link via the public endpoint. Only meaningful for UIF accounts —
  // AMP/AGC are filtered out server-side and get the same generic response.
  const [mode, setMode] = useState<'login' | 'forgot' | 'sent'>('login');
  const [resetEmail, setResetEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const result = await api.adminLogin({ username, password });
      login(result.token, result.user);
      window.location.hash = routeFor(result.user.role);
    } catch (err: any) {
      setError(err?.message || 'Login failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !resetEmail.trim()) return;
    setSubmitting(true); setError('');
    try {
      await api.requestPasswordReset(resetEmail.trim());
      setMode('sent');
    } catch (err: any) {
      setError(err?.message || 'Could not send reset link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
        <h1 className="text-2xl font-bold text-uif-green-dark mb-1">Sign in</h1>
        <p className="text-sm text-gray-500 mb-6">Up in Farms · Events platform</p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 mb-4 text-sm">
            {error}
          </div>
        )}

        {mode === 'login' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Username</span>
              <input
                type="text"
                required
                autoFocus
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Password</span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
              />
            </label>

            <div className="text-right">
              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(''); }}
                className="text-xs text-uif-green-dark hover:underline"
              >
                Forgot password? (UIF accounts only)
              </button>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-4 py-2 hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Sign in
              </button>
            </div>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgot} className="space-y-4">
            <p className="text-sm text-gray-700">
              Enter the email on your UIF admin account. If it matches an active UIF user we'll send a reset link valid for 1 hour.
            </p>
            <p className="text-xs text-gray-500 italic">
              AMP and AGC share an account and can't use this flow — contact UIF support to rotate that password.
            </p>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Email</span>
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
              />
            </label>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setMode('login'); setError(''); }}
                disabled={submitting}
                className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back to sign in
              </button>
              <button
                type="submit"
                disabled={submitting || !resetEmail.trim()}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-4 py-2 hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Send reset link
              </button>
            </div>
          </form>
        )}

        {mode === 'sent' && (
          <div className="space-y-4">
            <div className="bg-uif-green/5 border border-uif-green/30 rounded-md p-4 text-sm text-uif-green-dark">
              <strong>Check your email.</strong> If an active UIF account uses that address, a reset link is on the way. The link expires in 1 hour and can only be used once.
            </div>
            <button
              type="button"
              onClick={() => { setMode('login'); setResetEmail(''); }}
              className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
